import React, { useState, useEffect, useMemo, useRef, useCallback, startTransition } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  Alert,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Add,
  ViewList,
  CalendarMonth,
  Clear as ClearIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useOrganizationChange } from '@/hooks/useOrganizationChange';
import { useScrollFab } from '@/hooks/useScrollFab';
import { useAppSelector } from '@/hooks/redux';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

dayjs.extend(utc);

import { scheduleService, ScheduleMatch, ScheduleFilters } from '@/services/scheduleService';
import { playerService } from '@/services/playerService';
import { conflictService } from '@/services/conflictService';
import AddMatchDialog from '@/components/AddMatchDialog';
import EditMatchDialog from '@/components/EditMatchDialog';
import TBDMatchesSection from '@/components/TBDMatchesSection';
import type { MapMarker } from '@/components/mapTypes';
import FullCalendarDashboard from '@/components/FullCalendarDashboard';
import { VenueOverrideDialog } from '@/components/VenueOverrideDialog';
import { PlayerDepartureEntry } from '@/components/DepartureTimeDisplay';
import { formatInUserTimezone } from '@/utils/timezoneHelpers';
import type { DetectedConflict } from '@/services/conflictService';
import { ScheduleListSkeleton } from '@/components/ScheduleSkeletons';
import { WeatherDetailContent } from '@/components/WeatherDisplay';
import type { MatchWeatherData } from '@/services/scheduleService';
import { markAsRead, markAllAsRead } from '@/services/notificationService';
import { useNotifications } from '@/contexts/EventStreamContext';
import { clearPushNotifications } from '@/utils/clearPushNotifications';
import ScheduleMatchCard from '@/components/ScheduleMatchCard';
import VenuePopupContent from '@/components/VenuePopupContent';
import BottomDrawer from '@/components/BottomDrawer';
import ConflictPopupContent from '@/components/ConflictPopupContent';

// Isolated search input — keeps keystroke state local so the heavy page doesn't re-render on every keypress
const ScheduleSearchInput: React.FC<{ value: string; onChange: (v: string) => void }> = React.memo(({ value, onChange }) => {
  const [local, setLocal] = useState(value);
  const parentSyncRef = useRef(false);

  // Propagate to parent after short debounce (client-side filter, so 150ms is fine)
  const debouncedLocal = useDebouncedValue(local, 150);
  useEffect(() => {
    // Skip onChange when value was just synced from parent (avoids round-trip)
    if (parentSyncRef.current) {
      parentSyncRef.current = false;
      return;
    }
    onChange(debouncedLocal);
  }, [debouncedLocal, onChange]);

  // Sync local when parent sets value externally (e.g. notification deep link or clear)
  useEffect(() => {
    if (value !== local) {
      parentSyncRef.current = true;
      setLocal(value);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TextField
      size="small"
      placeholder="Filter by player, team, location, or league"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
      }}
      sx={{ flex: 1, minWidth: 0, width: '100%' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          </InputAdornment>
        ),
        ...(local && {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setLocal('')} edge="end">
                <ClearIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ),
        }),
      }}
    />
  );
});

// Build full address from match fields, avoiding duplicate city/state
// when match.address is already a formatted address like "201 Soccer Park Dr, Cary, NC 27511"
const buildFullAddress = (match: { address?: string; street?: string; city?: string; state?: string }): string => {
  if (match.street) {
    return [match.street, match.city, match.state].filter(Boolean).join(', ');
  }
  if (match.address) {
    return match.address;
  }
  return [match.city, match.state].filter(Boolean).join(', ');
};

// Whether a match is finished and should not show travel/departure times
const isMatchOver = (match: ScheduleMatch): boolean => {
  if (match.status === 'COMPLETED' || match.status === 'CANCELLED') return true;
  if (match.homeScore != null && match.awayScore != null) return true;
  if (match.status === 'SCHEDULED' && match.dateTime && dayjs(match.dateTime).add(2, 'hour').isBefore(dayjs())) return true;
  return false;
};

const SchedulePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fabRef = useScrollFab(100);
  const [searchParams, setSearchParams] = useSearchParams();
  const { decrementUnreadCount, resetUnreadCount } = useNotifications();
  
  // Set default date range for list view (starts from today)
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(3, 'month'));
  const [addMatchOpen, setAddMatchOpen] = useState(false);
  const [editMatchOpen, setEditMatchOpen] = useState(false);
  const [selectedMatch] = useState<ScheduleMatch | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [venueOverrideTarget, setVenueOverrideTarget] = useState<{ venueName: string; currentAddress?: string } | null>(null);
  const [venueDrawer, setVenueDrawer] = useState<{ venue: string; address: string; latitude?: number | null; longitude?: number | null; departureEntries?: PlayerDepartureEntry[]; markers?: MapMarker[]; matchTime?: string | null } | null>(null);
  const [addressCopied, setAddressCopied] = useState(false);
  const [weatherDrawer, setWeatherDrawer] = useState<{ weather: MatchWeatherData; matchTime: string | null } | null>(null);
  const [conflictDrawer, setConflictDrawer] = useState<{ match: ScheduleMatch; conflicts: DetectedConflict[]; dayConflicts: DetectedConflict[]; isTbd: boolean } | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterMatchId, setFilterMatchId] = useState<string | null>(null);
  
  // Separate initial filter values for each view mode
  const initialListFilters = useMemo(() => ({
    startDate: dayjs().format('YYYY-MM-DD'), // List view starts from today
    endDate: dayjs().add(3, 'month').format('YYYY-MM-DD'),
    playerIds: [] as string[],
  }), []);

  const initialCalendarFilters = useMemo(() => ({
    startDate: dayjs().subtract(6, 'month').format('YYYY-MM-DD'), // Calendar shows all matches
    endDate: dayjs().add(6, 'month').format('YYYY-MM-DD'),
    playerIds: [] as string[],
  }), []);

  // Separate filter states for each view mode
  const [listFilters, setListFilters] = useState<ScheduleFilters>(initialListFilters);
  const [calendarFilters] = useState<ScheduleFilters>(initialCalendarFilters);

  // Handle URL query params for deep linking from notifications
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam === 'calendar') {
      setViewMode('calendar');
    }

    const actionParam = searchParams.get('action');
    if (actionParam === 'add') {
      setAddMatchOpen(true);
    }

    const searchParam = searchParams.get('search');
    const dateParam = searchParams.get('date');
    const matchIdParam = searchParams.get('matchId');
    const notificationIdParam = searchParams.get('notificationId');
    const fromDigestParam = searchParams.get('fromDigest');

    // Mark notification as read if opened from email link
    // Chain decrementUnreadCount after markAsRead so server state is consistent
    // before SSE potentially reconnects with the updated count
    if (notificationIdParam) {
      clearPushNotifications();
      markAsRead([notificationIdParam])
        .then(() => decrementUnreadCount())
        .catch(() => decrementUnreadCount());
    } else if (matchIdParam) {
      // "View Match" from digest email without a notificationId — can't target by
      // tag so clear all push notifications for this app
      clearPushNotifications();
    }

    // Digest email link — mark all notifications as read and clear badge
    if (fromDigestParam) {
      clearPushNotifications();
      markAllAsRead()
        .then(() => resetUnreadCount())
        .catch(() => resetUnreadCount());
    }

    if (searchParam || dateParam || viewParam || actionParam || matchIdParam || notificationIdParam || fromDigestParam) {
      // Apply exact match filter if matchId is provided — clear date range
      // so the API returns all matches (the target may be outside the default window)
      if (matchIdParam) {
        setFilterMatchId(matchIdParam);
        setStartDate(null);
        setEndDate(null);
        setListFilters(prev => ({
          ...prev,
          startDate: undefined,
          endDate: undefined,
        }));
      }

      // Apply search text to filter the schedule
      if (searchParam) {
        setSearchText(searchParam);
      }

      // Apply date filter - set to just that day
      if (dateParam) {
        const targetDate = dayjs(dateParam);
        if (targetDate.isValid()) {
          setStartDate(targetDate);
          setEndDate(targetDate);
          setListFilters(prev => ({
            ...prev,
            startDate: targetDate.format('YYYY-MM-DD'),
            endDate: targetDate.format('YYYY-MM-DD'),
          }));
        }
      }

      // Clear the URL params after applying
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, decrementUnreadCount, resetUnreadCount]);

  // Use the appropriate filters based on current view mode (memoized to prevent infinite re-renders)
  const filters = useMemo(() => {
    return viewMode === 'calendar' ? calendarFilters : listFilters;
  }, [viewMode, calendarFilters, listFilters]);
  const queryClient = useQueryClient();
  const { user } = useAppSelector((state) => state.auth);

  const handleVenueOverrideSaved = useCallback(() => {
    queryClient.invalidateQueries(['schedule']);
    queryClient.invalidateQueries(['upcoming']);
  }, [queryClient]);

  // Fetch players for filter dropdown
  const { data: players = [] } = useQuery(
    ['players', user?.currentOrganizationId], 
    playerService.getPlayers,
    {
      enabled: !!user?.currentOrganizationId,
      keepPreviousData: true,
    }
  );

  // Refresh all queries when organization changes
  useOrganizationChange(() => {
    console.log('🔄 SchedulePage: Organization changed, clearing cache and refetching');
    console.log('Current org ID:', user?.currentOrganizationId);
    // Remove cached data completely and refetch
    queryClient.removeQueries(['schedule']);
    queryClient.removeQueries(['departureTimes']);
    queryClient.removeQueries(['weather']);
    queryClient.removeQueries(['players']);
    queryClient.removeQueries(['conflicts']);
    // Force immediate refetch
    queryClient.refetchQueries(['schedule']);
    queryClient.refetchQueries(['players']);
  });

  // Debug: Log current organization ID changes
  useEffect(() => {
    console.log('SchedulePage: Current organization ID:', user?.currentOrganizationId);
  }, [user?.currentOrganizationId]);

  // Fetch schedule
  const { data: matches = [], isLoading, error, isFetching } = useQuery(
    ['schedule', user?.currentOrganizationId, filters],
    () => {
      console.log('🔄 SchedulePage: Fetching schedule with filters:', filters);
      console.log('🔄 SchedulePage: Current org ID in query:', user?.currentOrganizationId);
      return scheduleService.getSchedule(filters);
    },
    {
      enabled: !!user?.currentOrganizationId,
      staleTime: 0, // Always consider stale - refetch when invalidated
      cacheTime: 1000 * 60 * 15, // 15 minutes - keep in memory
      refetchOnMount: true, // Refetch when component mounts
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      keepPreviousData: true, // Keep previous data while fetching new data
      onSuccess: (data) => {
        console.log('✅ SchedulePage: Schedule data fetched successfully, matches count:', data.length);
      },
      onError: (error) => {
        console.error('❌ SchedulePage: Schedule fetch error:', error);
      }
    }
  );

  // When filterMatchId is set with no search text (e.g. digest email "View Match"),
  // derive search text from the matched match so the search box shows context and
  // the user can clear the filter via the X button.
  useEffect(() => {
    if (filterMatchId && !searchText && matches.length > 0) {
      const match = matches.find(m => m.id === filterMatchId);
      if (match && match.homeTeam && match.awayTeam) {
        setSearchText(`${match.homeTeam} vs ${match.awayTeam}`);
      }
    }
  }, [filterMatchId, matches]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive match IDs for departure times query (only upcoming matches)
  const matchIds = useMemo(() => matches
    .filter(m => !isMatchOver(m))
    .filter(m => !m.players?.length || m.players.some(p => p.hasAddress !== false))
    .map(m => m.id), [matches]);

  // Fetch departure times separately for progressive loading
  const { data: departureTimesResponse, isLoading: departureTimesLoading } = useQuery(
    ['departureTimes', user?.currentOrganizationId, matchIds],
    () => scheduleService.getDepartureTimes(matchIds),
    {
      enabled: matchIds.length > 0 && !!user?.currentOrganizationId,
      staleTime: 1000 * 60 * 30,  // 30 minutes - DB-cached, no need to refetch often
      cacheTime: 1000 * 60 * 60, // 1 hour
      keepPreviousData: true,
    }
  );
  const departureTimesData = departureTimesResponse?.departureTimes;

  // If backend is computing departure times in the background, refetch after a delay
  // Cap retries to avoid infinite refetch loops if computation permanently fails
  const departureRetryCount = useRef(0);
  useEffect(() => {
    if (departureTimesResponse?.pending && departureTimesResponse.pending > 0 && departureRetryCount.current < 3) {
      const timer = setTimeout(() => {
        departureRetryCount.current += 1;
        queryClient.invalidateQueries(['departureTimes', user?.currentOrganizationId, matchIds]);
      }, 10000); // Refetch after 10 seconds
      return () => clearTimeout(timer);
    }
    // Reset retry count when pending drops to 0 (success)
    if (!departureTimesResponse?.pending || departureTimesResponse.pending === 0) {
      departureRetryCount.current = 0;
    }
  }, [departureTimesResponse?.pending, user?.currentOrganizationId, matchIds, queryClient]);

  // --- Weather forecasts (progressive loading, same pattern as departure times) ---
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const weatherMatchIds = useMemo(() => matches
    .filter(m => m.status !== 'COMPLETED' && m.status !== 'CANCELLED' && m.dateTime && m.status !== 'TBD' &&
      (new Date(m.dateTime).getTime() - Date.now()) <= SEVEN_DAYS_MS)
    .map(m => m.id), [matches]);

  const hasImminentMatch = useMemo(() =>
    matches.some(m => m.dateTime && !isMatchOver(m) &&
      (new Date(m.dateTime).getTime() - Date.now()) < 24 * 60 * 60 * 1000
    ), [matches]);

  const { data: weatherResponse, isLoading: weatherLoading } = useQuery(
    ['weather', user?.currentOrganizationId, weatherMatchIds],
    () => scheduleService.getWeather(weatherMatchIds),
    {
      enabled: weatherMatchIds.length > 0 && !!user?.currentOrganizationId,
      staleTime: hasImminentMatch ? 1000 * 60 * 5 : 1000 * 60 * 30,
      cacheTime: 1000 * 60 * 60,
      keepPreviousData: true,
    }
  );
  const weatherData = weatherResponse?.weather;
  // Track which specific match IDs the backend is still computing weather for
  const weatherPendingSet = useMemo(() => new Set(weatherResponse?.pendingMatchIds || []), [weatherResponse?.pendingMatchIds]);
  const weatherRetryCount = useRef(0);

  // If backend is computing weather in background, refetch after a delay
  useEffect(() => {
    if (weatherResponse?.pending && weatherResponse.pending > 0 && weatherRetryCount.current < 6) {
      const timer = setTimeout(() => {
        weatherRetryCount.current += 1;
        queryClient.invalidateQueries(['weather', user?.currentOrganizationId, weatherMatchIds]);
      }, 5000);
      return () => clearTimeout(timer);
    }
    if (!weatherResponse?.pending || weatherResponse.pending === 0) {
      weatherRetryCount.current = 0;
    }
  }, [weatherResponse?.pending, user?.currentOrganizationId, weatherMatchIds, queryClient]);

  // Debug: Track when matches data changes
  useEffect(() => {
    console.log('📊 SchedulePage: Matches data changed, count:', matches.length);
    if (matches.length > 0) {
      console.log('📊 SchedulePage: First few matches:', matches.slice(0, 3).map(m => ({
        id: m.id,
        homeTeam: m.homeTeam,
        awayTeam: m.awayTeam,
        players: (m.players || []).map(p => p.name)
      })));
    }
  }, [matches]);

  // Fetch conflicts for conflict indicators
  // Conflicts are pre-computed on the backend whenever the schedule changes,
  // so we just fetch the cached result here without triggering recalculation.
  const { data: conflictData } = useQuery(
    ['conflicts', user?.currentOrganizationId],
    () => conflictService.getConflicts(),
    {
      enabled: players.length > 0 && !!user?.currentOrganizationId,
      staleTime: 1000 * 60 * 5, // 5 minutes - backend caches until next schedule change
      cacheTime: 1000 * 60 * 15, // 15 minutes - keep conflicts cached longer
      keepPreviousData: true, // Show previous data while loading
      refetchOnWindowFocus: true,
    }
  );

  // Auto-apply date filters when dates change (only affects list view)
  useEffect(() => {
    handleDateChange();
  }, [startDate, endDate]);



  const handleDateChange = () => {
    // Date pickers only affect list view filters
    setListFilters(prev => ({
      ...prev,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
    }));
  };

  const isLikelyPlayed = (match: ScheduleMatch): boolean => {
    return match.status === 'SCHEDULED' && !!match.dateTime && dayjs(match.dateTime).add(2, 'hour').isBefore(dayjs());
  };

  const isLikelyInProgress = (match: ScheduleMatch): boolean => {
    if (match.status !== 'SCHEDULED' || !match.dateTime) return false;
    const start = dayjs(match.dateTime);
    const now = dayjs();
    return start.isBefore(now) && start.add(2, 'hour').isAfter(now);
  };

  // Get all player departure entries for a match (for multi-player display)
  const getAllDepartureEntriesForMatch = (match: ScheduleMatch): PlayerDepartureEntry[] => {
    if (!match.players || match.players.length === 0) return [];
    const matchDepartures = departureTimesData?.[match.id];
    if (!matchDepartures) return [];
    const entries: PlayerDepartureEntry[] = [];
    for (const player of match.players) {
      const info = matchDepartures[player.id];
      if (info) {
        entries.push({
          playerName: player.name,
          departureInfo: {
            departureTime: info.departureTime ? new Date(info.departureTime) : null,
            isApproximate: info.isApproximate,
            travelTimeMinutes: info.travelTimeMinutes,
            noTravelRequired: info.noTravelRequired,
            distanceMeters: info.distanceMeters,
            baseTravelTimeMinutes: info.baseTravelTimeMinutes,
            bufferMinutes: info.bufferMinutes,
            warmupMinutes: info.warmupMinutes,
            matchTime: new Date(match.dateTime!),
            origin: info.origin,
          },
        });
      }
    }
    return entries;
  };

  // Get map markers for a match — single marker for account holder's home address
  const getMapMarkersForMatch = (match: ScheduleMatch, _allMatches: ScheduleMatch[]): MapMarker[] => {
    if (!match.players || match.players.length === 0) return [];

    const matchDepartures = departureTimesData?.[match.id];

    // Try to get origin from departure data (first player with departure info)
    if (matchDepartures) {
      for (const player of match.players) {
        const departureTime = matchDepartures[player.id];
        if (!departureTime?.origin?.latitude || !departureTime?.origin?.longitude) continue;

        const origin = departureTime.origin;
        const originLat = origin.latitude!;
        const originLng = origin.longitude!;

        // Skip if origin is same location as match venue (within ~100m)
        const isSameLocation = match.latitude && match.longitude &&
          Math.abs(originLat - match.latitude) < 0.001 &&
          Math.abs(originLng - match.longitude) < 0.001;
        if (isSameLocation) return [];

        return [{
          latitude: originLat,
          longitude: originLng,
          type: origin.type === 'home' ? 'home' as const : 'previous' as const,
          color: '#0173B2',
          label: origin.type === 'home' ? 'Home' : origin.location || 'Previous venue',
          distanceMeters: departureTime.distanceMeters,
        }];
      }
    }

    // Fallback: use first player's home coordinates
    for (const player of match.players) {
      if (!player.homeLatitude || !player.homeLongitude) continue;

      // Skip if home is same location as match venue (within ~100m)
      const isSameLocation = match.latitude && match.longitude &&
        Math.abs(player.homeLatitude - match.latitude) < 0.001 &&
        Math.abs(player.homeLongitude - match.longitude) < 0.001;
      if (isSameLocation) return [];

      return [{
        latitude: player.homeLatitude,
        longitude: player.homeLongitude,
        type: 'home' as const,
        color: '#0173B2',
        label: 'Home',
      }];
    }

    return [];
  };



  const formatMatchDate = (dateTime: string | null, status?: string, match?: any) => {
    // If no dateTime but has date field, use the date field
    if (!dateTime && match?.date) {
      return dayjs.utc(match.date).format('MMM D, YYYY');
    }

    if (!dateTime) return 'TBD';

    // For TBD matches or unverified timezone, use UTC to display literal stored time
    if (status === 'TBD' || match?.timezoneVerified === false) {
      return dayjs.utc(dateTime).format('MMM D, YYYY');
    }

    return formatInUserTimezone(dateTime, 'MMM D, YYYY', false);
  };

  const formatMatchDateWithDay = (dateTime: string | null, status?: string, match?: any) => {
    // If no dateTime but has date field, use the date field
    if (!dateTime && match?.date) {
      return dayjs.utc(match.date).format('dddd, MMM D, YYYY');
    }

    if (!dateTime) return 'TBD';

    // For TBD matches or unverified timezone, use UTC to display literal stored time
    if (status === 'TBD' || match?.timezoneVerified === false) {
      return dayjs.utc(dateTime).format('dddd, MMM D, YYYY');
    }

    return formatInUserTimezone(dateTime, 'dddd, MMM D, YYYY', false);
  };

  // Get all conflicts for a specific date
  const getConflictsForDate = (date: string): any[] => {
    if (!filteredConflictData?.conflictGroups) return [];

    if (date === 'TBD') return [];

    const dateObj = dayjs(date, 'MMM D, YYYY');
    const conflictGroup = filteredConflictData.conflictGroups.find(group => {
      const groupDate = dayjs.utc(group.date);
      return groupDate.isSame(dateObj, 'day');
    });

    return conflictGroup ? conflictGroup.conflicts : [];
  };

  const getMatchConflictSeverity = (match: any, date: string): string | null => {
    if (!filteredConflictData?.conflictGroups) return null;

    // TBD matches always show yellow — they could conflict once scheduled
    if (match.status === 'TBD' || date === 'TBD') {
      return 'MODERATE';
    }

    const dateObj = dayjs(date, 'MMM D, YYYY');
    const conflictGroup = filteredConflictData.conflictGroups.find(group => {
      const groupDate = dayjs.utc(group.date);
      return groupDate.isSame(dateObj, 'day');
    });

    if (!conflictGroup) return null;

    // For non-TBD matches, check if involved in any conflicts and return day's highest severity
    const isMatchInConflict = conflictGroup.conflicts.some(conflict =>
      conflict.match1.match.id === match.id || conflict.match2.match.id === match.id
    );

    return isMatchInConflict ? conflictGroup.severity : null;
  };

  const getConflictBorderColor = (severity: string | null): string => {
    switch (severity) {
      case 'DIRECT':
        return '#dc2626'; // Red
      case 'MODERATE':
        return '#d97706'; // Amber
      case 'MINIMAL':
        return '#2563eb'; // Blue
      default:
        return '#4caf50'; // Green (no conflict)
    }
  };

  const getConflictsForMatch = (match: ScheduleMatch, date: string): DetectedConflict[] => {
    // TBD matches have no backend conflicts — yellow indicator is purely visual
    if (match.status === 'TBD' || date === 'TBD') {
      return [];
    }
    const dayConflicts = getConflictsForDate(date);
    return dayConflicts.filter(
      (c: DetectedConflict) => c.match1.match.id === match.id || c.match2.match.id === match.id
    );
  };

  const formatMatchTime = (dateTime: string | null, status?: string, match?: any): string => {
    if (!dateTime || status === 'TBD') return 'TBD';
    return match?.timezoneVerified === false
      ? dayjs.utc(dateTime).format('h:mm A')
      : formatInUserTimezone(dateTime, 'h:mm A', false);
  };

  const groupMatchesByDate = (matches: ScheduleMatch[]) => {
    const grouped: { [key: string]: ScheduleMatch[] } = {};
    matches.forEach(match => {
      const date = formatMatchDate(match.dateTime, match.status, match);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(match);
    });
    
    // Sort TBD matches to appear at the end
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (a === 'TBD') return 1;
      if (b === 'TBD') return -1;
      return dayjs(a).isBefore(dayjs(b)) ? -1 : 1;
    });
    
    const sortedGrouped: { [key: string]: ScheduleMatch[] } = {};
    sortedKeys.forEach(key => {
      // Sort matches within each date: TBD matches first, then by time
      const sortedMatches = grouped[key].sort((a, b) => {
        if (!a.dateTime && !b.dateTime) return 0;
        if (!a.dateTime) return -1; // TBD matches first
        if (!b.dateTime) return 1;
        
        const timeA = dayjs(a.dateTime);
        const timeB = dayjs(b.dateTime);
        
        // Check if either is TBD
        const isATBD = a.status === 'TBD';
        const isBTBD = b.status === 'TBD';
        
        if (isATBD && isBTBD) return 0;
        if (isATBD) return -1; // TBD matches first
        if (isBTBD) return 1;
        
        // Sort by actual time
        return timeA.isBefore(timeB) ? -1 : 1;
      });
      
      sortedGrouped[key] = sortedMatches;
    });
    
    return sortedGrouped;
  };

  // Clear filterMatchId when user clears or changes search
  // Use a ref to keep the callback stable — avoids ScheduleSearchInput's debounce
  // effect re-firing when filterMatchId changes (which would immediately clear it)
  const filterMatchIdRef = useRef(filterMatchId);
  filterMatchIdRef.current = filterMatchId;
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const handleSearchTextChange = useCallback((value: string) => {
    const prev = searchTextRef.current;
    setSearchText(value);
    if (filterMatchIdRef.current) {
      // User clicked X to clear search, or typed something different
      if ((prev.length > 0 && value.length === 0) || (value.length > 0 && value !== prev)) {
        setFilterMatchId(null);
        // Reset date range back to defaults when clearing
        if (value.length === 0) {
          setStartDate(dayjs());
          setEndDate(dayjs().add(3, 'month'));
          setListFilters(initialListFilters);
        }
      }
    }
  }, []);

  // Apply client-side filtering for player selection (must be before early returns)
  const filteredMatches = useMemo(() => {
    let result = matches;

    // Exact match filter (from notification deep link)
    if (filterMatchId) {
      const exactMatch = result.filter(m => m.id === filterMatchId);
      if (exactMatch.length > 0) return exactMatch;
      // If not found in current results, fall through to normal filtering
    }

    // Player filter
    if (filters.playerIds && filters.playerIds.length > 0) {
      result = result.filter(match =>
        match.players && match.players.some(player =>
          filters.playerIds!.includes(player.id)
        )
      );
    }

    // Text filter (client-side, case-insensitive)
    if (searchText.length >= 2) {
      const term = searchText.toLowerCase();
      result = result.filter(match =>
        (match.homeTeam && match.homeTeam.toLowerCase().includes(term)) ||
        (match.awayTeam && match.awayTeam.toLowerCase().includes(term)) ||
        (match.location && match.location.toLowerCase().includes(term)) ||
        (match.address && match.address.toLowerCase().includes(term)) ||
        (match.city && match.city.toLowerCase().includes(term)) ||
        (match.state && match.state.toLowerCase().includes(term)) ||
        (match.players && match.players.some(p => p.name.toLowerCase().includes(term))) ||
        (match.league?.name && match.league.name.toLowerCase().includes(term)) ||
        (match.league?.division && match.league.division.toLowerCase().includes(term))
      );
    }

    return result;
  }, [matches, filters.playerIds, searchText, filterMatchId]);

  // Filter conflict data based on active player filters and search text
  const filteredConflictData = useMemo(() => {
    if (!conflictData) return conflictData;

    // Build the set of player IDs visible in filtered matches
    const visiblePlayerIds = new Set<string>();
    for (const match of filteredMatches) {
      if (match.players) {
        for (const p of match.players) visiblePlayerIds.add(p.id);
      }
    }

    const activePlayerIds = filters.playerIds;
    const hasPlayerFilter = activePlayerIds && activePlayerIds.length > 0;
    const hasSearch = searchText.length >= 2;

    // Nothing to narrow — return as-is
    if (!hasPlayerFilter && !hasSearch) return conflictData;

    const shouldInclude = (playerId: string) => {
      if (hasPlayerFilter && !activePlayerIds!.includes(playerId)) return false;
      if (hasSearch && !visiblePlayerIds.has(playerId)) return false;
      return true;
    };

    // Filter each conflict group's conflicts to only include those involving visible players
    const filterGroups = (groups: typeof conflictData.conflictGroups) =>
      groups
        .map(group => {
          const filtered = group.conflicts.filter(c =>
            shouldInclude(c.match1.playerId) || shouldInclude(c.match2.playerId)
          );
          if (filtered.length === 0) return null;
          const severity = filtered.some(c => c.severity === 'DIRECT') ? 'DIRECT' as const
            : filtered.some(c => c.severity === 'MODERATE') ? 'MODERATE' as const
            : 'MINIMAL' as const;
          return { ...group, conflicts: filtered, severity };
        })
        .filter((g): g is NonNullable<typeof g> => g !== null);

    const filteredGroups = filterGroups(conflictData.conflictGroups);
    const filteredUpcoming = filterGroups(conflictData.upcomingConflicts);

    // Recompute summary counts
    const allFilteredConflicts = filteredGroups.flatMap(g => g.conflicts);
    const bySeverity: Record<string, number> = {};
    allFilteredConflicts.forEach(c => {
      bySeverity[c.severity] = (bySeverity[c.severity] || 0) + 1;
    });

    return {
      ...conflictData,
      totalConflicts: allFilteredConflicts.length,
      conflictGroups: filteredGroups,
      upcomingConflicts: filteredUpcoming,
      summary: {
        ...conflictData.summary,
        total: allFilteredConflicts.length,
        bySeverity,
        conflictDays: filteredGroups.length,
        upcomingCount: filteredUpcoming.length,
      },
    };
  }, [conflictData, filters.playerIds, filteredMatches, searchText]);

  // Helper function to check if a match should go in the pending section
  const shouldGoInPendingSection = (match: any) => {
    // Matches with no dateTime AND no date go to pending section
    // Also include POSTPONED matches - they may have date/time but shouldn't be on calendar
    return (!match.dateTime && !match.date) || match.status === 'POSTPONED';
  };

  // Separate pending matches (TBD + Postponed) from scheduled matches (must be before early returns)
  const tbdMatches = filteredMatches.filter(match => shouldGoInPendingSection(match));
  const scheduledMatches = filteredMatches.filter(match => !shouldGoInPendingSection(match));
  const groupedScheduledMatches = groupMatchesByDate(scheduledMatches);

  // Incremental rendering: show a limited number of cards initially, load more on scroll
  const INITIAL_VISIBLE = 8;
  const LOAD_MORE_COUNT = 8;
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Reset visible count when filters/data change
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [filters, searchText, filterMatchId]);

  // IntersectionObserver to load more cards as user scrolls near bottom
  // Wrapped in startTransition so new-card rendering is non-blocking (won't jank scroll)
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startTransition(() => {
            setVisibleCount(prev => prev + LOAD_MORE_COUNT);
          });
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [groupedScheduledMatches]);

  // Slice grouped matches to only render visibleCount total cards
  const { visibleGroups, hasMore } = useMemo(() => {
    const result: Record<string, typeof scheduledMatches> = {};
    let count = 0;
    for (const [date, dayMatches] of Object.entries(groupedScheduledMatches)) {
      if (count >= visibleCount) break;
      const remaining = visibleCount - count;
      const slice = dayMatches.slice(0, remaining);
      result[date] = slice;
      count += slice.length;
    }
    return { visibleGroups: result, hasMore: count < scheduledMatches.length };
  }, [groupedScheduledMatches, visibleCount, scheduledMatches.length]);

  // Prefetch calendar data when on list view so calendar tab loads instantly
  useEffect(() => {
    if (viewMode === 'list' && user?.currentOrganizationId) {
      const currentMonthKey = dayjs().format('YYYY-MM');
      const calendarStartDate = dayjs().subtract(6, 'month').format('YYYY-MM-DD');
      const calendarEndDate = dayjs().add(6, 'month').format('YYYY-MM-DD');

      queryClient.prefetchQuery(
        ['schedule-calendar', currentMonthKey],
        () => scheduleService.getSchedule({
          startDate: calendarStartDate,
          endDate: calendarEndDate,
          playerIds: [],
        }),
        {
          staleTime: 1000 * 60 * 5,
        }
      );
    }
  }, [viewMode, user?.currentOrganizationId, queryClient]);

  // Don't block card rendering on players query — cards show immediately,
  // player color map and conflicts fill in progressively when ready
  const matchesLoading = isLoading;

  if (error) {
    return (
      <Container>
        <Alert severity="error">
          Failed to load schedule. Please try again.
        </Alert>
      </Container>
    );
  }



  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth={false} sx={{ py: 2, px: { xs: 1.5, sm: 3 }, pb: { xs: 8, sm: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1, sm: 4 }}>
          <Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="h4" component="h1">
                Schedule
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              See your matches.
            </Typography>
            {/* Add Match Button - Desktop */}
            {!isMobile && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddMatchOpen(true)}
                size="large"
              >
                Add Match
              </Button>
            )}
          </Box>
          
          {/* View Toggle - right side, aligned with title */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newView) => newView && setViewMode(newView)}
            size="small"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewList />
              <Typography variant="body2" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
                List
              </Typography>
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="calendar view">
              <CalendarMonth />
              <Typography variant="body2" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
                Calendar
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Filter Bar: search + date pickers (list view only) */}
        <Box
          sx={{
            mb: { xs: 3, sm: 4 },
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 1 },
          }}
        >
          <ScheduleSearchInput value={searchText} onChange={handleSearchTextChange} />

          {viewMode === 'list' && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
              <DatePicker
                label="From"
                value={startDate}
                onChange={(v) => { setStartDate(v); if (filterMatchId) { setFilterMatchId(null); setSearchText(''); } }}
                desktopModeMediaQuery="@media (min-width: 0px)"
                slotProps={{ textField: { size: 'small', sx: { width: { xs: '50%', sm: 150 } } } }}
              />
              <DatePicker
                label="To"
                value={endDate}
                onChange={(v) => { setEndDate(v); if (filterMatchId) { setFilterMatchId(null); setSearchText(''); } }}
                desktopModeMediaQuery="@media (min-width: 0px)"
                slotProps={{ textField: { size: 'small', sx: { width: { xs: '50%', sm: 150 } } } }}
              />
            </Box>
          )}

        </Box>

        {/* TBD/Pending Matches Section - Show above list view */}
        {viewMode !== 'calendar' && <TBDMatchesSection matches={tbdMatches} />}

        {/* Conditional Content Based on View Mode */}
        {viewMode === 'calendar' ? (
          /* Calendar View */
          <FullCalendarDashboard
              filteredMatches={filteredMatches}
              isLoading={isLoading || isFetching}
            />
        ) : (
          /* List View */
          <Box sx={{ minHeight: '60vh' }}>
        {matchesLoading ? (
          <ScheduleListSkeleton />
        ) : matches.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No matches found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or add players to leagues to see their schedules
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Box>
            {Object.entries(visibleGroups).map(([date, dayMatches]) => {
              const formattedDate = date !== 'TBD' ? formatMatchDateWithDay(dayMatches[0]?.dateTime, dayMatches[0]?.status, dayMatches[0]) : 'TBD';

              return (
                <Box key={date} mb={{ xs: '20px', md: '26px' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={{ xs: '10px', md: '12px' }}>
                    <Typography sx={{ fontSize: { xs: '22px', md: '24px' }, fontWeight: 800, color: '#16a34a' }}>
                      {formattedDate}
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    {dayMatches.map((match) => {
                      const conflictSeverity = getMatchConflictSeverity(match, date);
                      const borderColor = getConflictBorderColor(conflictSeverity);
                      const played = isLikelyPlayed(match);
                      const inProgress = isLikelyInProgress(match);
                      const depEntries = (!isMatchOver(match) && !departureTimesLoading) ? getAllDepartureEntriesForMatch(match) : [];

                      return (
                        <Grid item xs={12} key={match.id}>
                          <ScheduleMatchCard
                            match={match}
                            date={date}
                            weather={weatherData?.[match.id]}
                            weatherLoading={(weatherLoading || weatherPendingSet.has(match.id)) && !weatherData?.[match.id]}
                            departureEntries={depEntries}
                            departureTimesLoading={departureTimesLoading}
                            departureTimesPending={!!departureTimesResponse?.pending && departureTimesResponse.pending > 0}
                            conflictSeverity={conflictSeverity}
                            conflictCount={getConflictsForMatch(match, date).length || (match.status === 'TBD' || date === 'TBD' ? 1 : 0)}
                            isPlayed={played}
                            isInProgress={inProgress}
                            isMatchOver={isMatchOver(match)}
                            borderColor={borderColor}
                            formatMatchTime={formatMatchTime}
                            onWeatherClick={() => {
                              window.getSelection()?.removeAllRanges();
                              if (!weatherData?.[match.id]?.forecastAvailable) return;
                              setWeatherDrawer({ weather: weatherData![match.id], matchTime: match.dateTime });
                            }}
                            onVenueClick={() => {
                              window.getSelection()?.removeAllRanges();
                              const fullAddr = buildFullAddress(match);
                              const markers = getMapMarkersForMatch(match, filteredMatches);
                              setVenueDrawer({ venue: match.location || 'Venue', address: fullAddr, latitude: match.latitude, longitude: match.longitude, departureEntries: depEntries.length > 0 ? depEntries : undefined, markers, matchTime: match.dateTime });
                            }}
                            onConflictClick={() => {
                              window.getSelection()?.removeAllRanges();
                              const isTbd = match.status === 'TBD' || date === 'TBD';
                              const matchConflicts = getConflictsForMatch(match, date);
                              const dayConflicts = getConflictsForDate(date);
                              setConflictDrawer({ match, conflicts: matchConflicts, dayConflicts, isTbd });
                            }}
                            onSetAddress={match.location && match.location !== 'TBD' && !match.venueOverride ? (e: React.MouseEvent) => {
                              e.preventDefault(); e.stopPropagation();
                              setVenueOverrideTarget({ venueName: match.location! });
                            } : undefined}
                            onEditAddress={match.venueOverride ? (e: React.MouseEvent) => {
                              e.preventDefault(); e.stopPropagation();
                              setVenueOverrideTarget({ venueName: match.location!, currentAddress: buildFullAddress(match) });
                            } : undefined}
                            buildFullAddress={buildFullAddress}
                          />

                    </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
            })}
            {hasMore && <Box ref={loadMoreRef} sx={{ height: 1 }} />}
          </Box>
        )}
          </Box>
        )}

        {/* Floating Action Button - Mobile Only */}
        {isMobile && (
          <Fab
            ref={fabRef}
            color="primary"
            aria-label="add match"
            sx={{
              position: 'fixed',
              bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))', // 56px nav + 16px margin + safe area
              right: 16,
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onClick={() => setAddMatchOpen(true)}
          >
            <Add />
          </Fab>
        )}

        {/* Add Match Dialog */}
        <AddMatchDialog
          open={addMatchOpen}
          onClose={() => setAddMatchOpen(false)}
        />

        {/* Edit Match Dialog */}
        <EditMatchDialog
          open={editMatchOpen}
          onClose={() => setEditMatchOpen(false)}
          match={selectedMatch}
        />
        {/* Venue drawer/modal */}
        <BottomDrawer open={!!venueDrawer} onClose={() => { setVenueDrawer(null); setAddressCopied(false); }} maxWidth={440}>
          {venueDrawer && (
            <VenuePopupContent
              venue={venueDrawer.venue}
              address={venueDrawer.address}
              latitude={venueDrawer.latitude}
              longitude={venueDrawer.longitude}
              departureEntries={venueDrawer.departureEntries}
              markers={venueDrawer.markers}
              distanceUnit={user?.distanceUnit}
              matchTime={venueDrawer.matchTime}
              copied={addressCopied}
              onCopyAddress={() => {
                navigator.clipboard.writeText(venueDrawer.address).then(() => {
                  setAddressCopied(true);
                  setTimeout(() => setAddressCopied(false), 1500);
                });
              }}
              onDirections={() => {
                if (venueDrawer.latitude && venueDrawer.longitude) {
                  const url = `https://maps.google.com/maps?daddr=${venueDrawer.latitude},${venueDrawer.longitude}`;
                  if (isMobile) {
                    // Close drawer before navigating so returning via back button
                    // doesn't leave a stale backdrop/black square behind the drawer
                    setVenueDrawer(null);
                    setAddressCopied(false);
                    window.location.href = url;
                  } else {
                    window.open(url, '_blank');
                  }
                }
              }}
            />
          )}
        </BottomDrawer>
        {/* Weather drawer/modal */}
        <BottomDrawer open={!!weatherDrawer} onClose={() => setWeatherDrawer(null)} maxWidth={400}>
          {weatherDrawer && (
            <WeatherDetailContent weather={weatherDrawer.weather} matchTime={weatherDrawer.matchTime} />
          )}
        </BottomDrawer>
        {/* Conflict drawer/modal */}
        <BottomDrawer open={!!conflictDrawer} onClose={() => setConflictDrawer(null)} maxWidth={440}>
          {conflictDrawer && (
            <ConflictPopupContent
              match={conflictDrawer.match}
              conflicts={conflictDrawer.conflicts}
              dayConflicts={conflictDrawer.dayConflicts}
              isTbd={conflictDrawer.isTbd}
            />
          )}
        </BottomDrawer>
        {/* Venue Override Dialog */}
        {venueOverrideTarget && (
          <VenueOverrideDialog
            open={!!venueOverrideTarget}
            onClose={() => setVenueOverrideTarget(null)}
            venueName={venueOverrideTarget.venueName}
            currentAddress={venueOverrideTarget.currentAddress}
            onSaved={handleVenueOverrideSaved}
          />
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default SchedulePage;
